import React, { useEffect, useState } from "react";
import moment from "moment";
import { ReloadOutlined } from "@ant-design/icons";
import {
    Button,
    DataTable,
    DataTableColumn,
    DataTableEntityCell,
    DataTableFilter,
    Tag,
    Tooltip,
    message,
} from "../../../design-system";
import {
    listNotificationDeliveries,
    retryNotificationDelivery,
} from "../../../common/api/AdminDashboardClient";
import { handleError } from "../../../common/ErrorHandling";
import { getRelativeTime } from "../../date/DateOp";
import {
    DeliveryStatus,
    NotificationDelivery,
    NotificationType,
} from "../../../types/NotificationTypes";
import classes from "./OrganizationsView.module.less";

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;

const TYPE_LABELS: Record<NotificationType, string> = {
    [NotificationType.OWNER_ASSIGNED]: "Owner assigned",
    [NotificationType.COLLABORATOR_ADDED]: "Shared",
    [NotificationType.COLLABORATOR_REMOVED]: "Access removed",
    [NotificationType.ANALYSIS_SUBMITTED]: "Analysis requested",
    [NotificationType.ANALYSIS_UPDATED]: "Analysis updated",
    [NotificationType.CHANGE_REQUESTED]: "Changes requested",
    [NotificationType.ANALYSIS_COMPLETED]: "Results ready",
    [NotificationType.OUTPUT_UPDATED]: "Results updated",
    [NotificationType.PROJECT_CLOSED]: "Project closed",
};

const STATUS_TAG: Record<DeliveryStatus, { label: string; color: string }> = {
    [DeliveryStatus.QUEUED]: { label: "Queued", color: "processing" },
    [DeliveryStatus.SENT]: { label: "Sent", color: "success" },
    [DeliveryStatus.FAILED]: { label: "Failed", color: "warning" },
    [DeliveryStatus.ABANDONED]: { label: "Abandoned", color: "error" },
};

// The segmenter's filters and the search are applied by the server: each
// one reloads the list, and no count is shown since the rows loaded say
// nothing about the rest.
const STATUS_FILTERS: (DataTableFilter<NotificationDelivery> & { status?: DeliveryStatus })[] = [
    { key: "all", label: "All", count: false },
    { key: DeliveryStatus.QUEUED, label: "Queued", status: DeliveryStatus.QUEUED, count: false },
    { key: DeliveryStatus.FAILED, label: "Failed", status: DeliveryStatus.FAILED, count: false },
    {
        key: DeliveryStatus.ABANDONED,
        label: "Abandoned",
        status: DeliveryStatus.ABANDONED,
        count: false,
    },
    { key: DeliveryStatus.SENT, label: "Sent", status: DeliveryStatus.SENT, count: false },
];

/**
 * Every organization's notification deliveries (plans/notifications.md,
 * section 4): what was sent to whom, what failed and why, with a retry.
 */
export const DeliveriesView: React.FC = () => {
    const [filterKey, setFilterKey] = useState("all");
    // The search box's text, and the text searched: the latter follows the
    // former once typing pauses, so the list does not reload on each key.
    const [query, setQuery] = useState("");
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState<NotificationDelivery[]>([]);
    const [next, setNext] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    const status = STATUS_FILTERS.find((f) => f.key === filterKey)?.status;

    useEffect(() => {
        const timer = window.setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [query]);

    const load = async (before: number | null) => {
        setLoading(true);
        try {
            const page = await listNotificationDeliveries(PAGE_SIZE, before, status, search);
            setRows((previous) => (before == null ? page.data : [...previous, ...page.data]));
            setNext(page.next);
        } catch (error: any) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, search]);

    const retry = async (delivery: NotificationDelivery) => {
        try {
            const retried = await retryNotificationDelivery(delivery.id);
            setRows((previous) => previous.map((row) => (row.id === retried.id ? retried : row)));
            void message.success(`Delivery to ${delivery.recipientEmail} queued again`);
        } catch (error: any) {
            handleError(error);
        }
    };

    const columns: DataTableColumn<NotificationDelivery>[] = [
        {
            key: "recipient",
            title: "Recipient",
            sort: "text",
            sortValue: (row) => row.recipientName,
            render: (row) => (
                <DataTableEntityCell
                    imageFallback={row.recipientName.charAt(0).toUpperCase()}
                    name={row.recipientName}
                    detail={row.recipientEmail}
                />
            ),
        },
        {
            key: "organization",
            title: "Organization",
            sort: "text",
            sortValue: (row) => row.organizationName,
            // No column filter: it would only see the rows loaded so far.
            // The search box reaches every organization.
            render: (row) => row.organizationName,
        },
        {
            key: "notification",
            title: "Notification",
            render: (row) => (
                <>
                    <div>{TYPE_LABELS[row.type]}</div>
                    <div className={classes.detailsHint}>
                        {row.projectName || "Unnamed project"}
                    </div>
                </>
            ),
        },
        {
            key: "status",
            title: "Status",
            width: "120px",
            render: (row) => (
                <Tag color={STATUS_TAG[row.status].color}>{STATUS_TAG[row.status].label}</Tag>
            ),
        },
        {
            key: "attempts",
            title: "Attempts",
            width: "90px",
            align: "right",
            render: (row) => row.attempts,
        },
        {
            key: "error",
            title: "Last error",
            render: (row) =>
                row.lastError ? (
                    <Tooltip title={row.lastError}>
                        <span className={classes.detailsHint}>
                            {row.lastError.length > 60
                                ? `${row.lastError.slice(0, 60)}...`
                                : row.lastError}
                        </span>
                    </Tooltip>
                ) : null,
        },
        {
            key: "when",
            title: "When",
            width: "150px",
            align: "right",
            sort: "date",
            sortValue: (row) => new Date(row.sentDate ?? row.recordCreationDate),
            render: (row) => {
                const date = new Date(row.sentDate ?? row.recordCreationDate);
                return (
                    <Tooltip title={moment(date).format("LLL")}>
                        <span>{getRelativeTime(date)}</span>
                    </Tooltip>
                );
            },
        },
        {
            key: "actions",
            title: "Actions",
            actions: true,
            // The deal cards' kind of action button: a bordered square with
            // an icon. Any delivery can go again, a sent one included, for
            // the recipient who says it never came.
            render: (row) => (
                <Tooltip title="Send again" mouseEnterDelay={0}>
                    <Button
                        icon={<ReloadOutlined />}
                        aria-label="Send again"
                        onClick={() => retry(row)}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <h1 className={classes.title}>Notifications</h1>
            </div>
            <div className={classes.subtitle}>
                Every email the notifications sent, across organizations, and what failed.
            </div>
            <DataTable<NotificationDelivery>
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                filters={STATUS_FILTERS}
                defaultFilter="all"
                onFilterChange={setFilterKey}
                search={{ value: query, onChange: setQuery }}
                searchPlaceholder="Search by recipient, organization or project"
                emptyText={loading ? "Loading" : "No deliveries"}
                storageKey="admin-deliveries"
            />
            {next != null && (
                <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
                    <Button loading={loading} onClick={() => load(next)}>
                        Show more
                    </Button>
                </div>
            )}
        </div>
    );
};
